import { useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { AlertTriangle, FileCheck, RotateCcw } from 'lucide-react';

export const TravelWaiver = ({ tripTitle, onAccept, onCancel, loading }) => {
  const [typedName, setTypedName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPhoto, setAgreedToPhoto] = useState(false);
  const [agreedToLiability, setAgreedToLiability] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const sigCanvas = useRef(null);

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setHasSignature(false);
  };

  const handleSignatureEnd = () => {
    // Check if canvas has any content drawn on it
    // Use requestAnimationFrame to ensure the canvas has been updated
    requestAnimationFrame(() => {
      if (sigCanvas.current) {
        const isEmpty = sigCanvas.current.isEmpty();
        console.log('Signature onEnd - isEmpty:', isEmpty);
        if (!isEmpty) {
          setHasSignature(true);
        }
      }
    });
  };

  // Track if user has interacted with the signature canvas
  const handleSignatureBegin = () => {
    console.log('Signature drawing started');
  };

  // Backup check when user releases mouse/touch on canvas
  const handleCanvasMouseUp = () => {
    // Double-check signature status after a short delay
    setTimeout(() => {
      if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
        setHasSignature(true);
        console.log('Signature detected via mouse/touch up');
      }
    }, 100);
  };

  const isComplete = typedName.trim().length > 2 && 
                     agreedToTerms && 
                     agreedToPhoto && 
                     agreedToLiability && 
                     hasSignature;
  
  // Signature is required
  const hasCompletedSignature = hasSignature;

  const handleAccept = () => {
    if (!isComplete) {
      console.log('Form incomplete:', { typedName, agreedToTerms, agreedToPhoto, agreedToLiability });
      return;
    }
    
    // Get signature if available
    let signatureData = null;
    try {
      if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
        signatureData = sigCanvas.current.toDataURL('image/png');
      }
    } catch (e) {
      console.warn('Could not get signature:', e);
    }
    
    console.log('Submitting waiver with signature:', !!signatureData);
    
    onAccept({
      typedName: typedName.trim(),
      signatureImage: signatureData,
      agreedAt: new Date().toISOString(),
      agreements: {
        terms: agreedToTerms,
        photoRelease: agreedToPhoto,
        liabilityWaiver: agreedToLiability
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
        <CardHeader className="border-b flex-shrink-0">
          <CardTitle className="flex items-center gap-2 text-primary">
            <FileCheck className="h-5 w-5" />
            Travel Waiver & Agreement
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            For: {tripTitle}
          </p>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-hidden p-0">
          <ScrollArea className="h-[60vh] p-6">
            {/* Travel Insurance Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800 mb-1">Travel Insurance Strongly Recommended</h4>
                  <p className="text-sm text-amber-700">
                    We strongly encourage all travelers to purchase comprehensive travel insurance that includes 
                    <strong> medical coverage, emergency evacuation, and trip cancellation protection</strong>. 
                    Travel to international destinations may involve risks, and adequate insurance coverage is 
                    essential for your protection. Circulate Travel is not responsible for any medical expenses, 
                    evacuation costs, or losses incurred due to lack of insurance coverage.
                  </p>
                </div>
              </div>
            </div>

            {/* Waiver Content */}
            <div className="space-y-6 text-sm text-muted-foreground">
              {/* Section 1: Assumption of Risk */}
              <section>
                <h3 className="font-semibold text-primary mb-2">1. Assumption of Risk & Liability Waiver</h3>
                <p className="mb-2">
                  I understand that participation in travel activities, tours, and excursions arranged by Circulate Travel 
                  involves inherent risks, including but not limited to:
                </p>
                <ul className="list-disc pl-5 space-y-1 mb-2">
                  <li>Physical injury, illness, or death</li>
                  <li>Property damage or loss</li>
                  <li>Travel delays, cancellations, or changes beyond our control</li>
                  <li>Political instability, natural disasters, or pandemics</li>
                  <li>Risks associated with adventure activities, transportation, and accommodations</li>
                </ul>
                <p>
                  I voluntarily assume all risks associated with my participation and agree to release, indemnify, 
                  and hold harmless Circulate Travel, its owners, employees, agents, and affiliates from any and all 
                  claims, damages, losses, or expenses arising from my participation, except in cases of gross 
                  negligence or willful misconduct.
                </p>
              </section>

              {/* Section 2: Medical Acknowledgment */}
              <section>
                <h3 className="font-semibold text-primary mb-2">2. Medical Acknowledgment</h3>
                <p>
                  I confirm that I am physically and mentally fit to participate in this trip. I understand that 
                  Circulate Travel does not provide medical advice and that I am responsible for consulting with 
                  healthcare professionals regarding vaccinations, medications, and fitness for travel. I agree to 
                  disclose any medical conditions that may affect my participation or require special accommodations.
                </p>
              </section>

              {/* Section 3: Photo/Video Release */}
              <section>
                <h3 className="font-semibold text-primary mb-2">3. Photo & Video Release</h3>
                <p className="mb-2">
                  I grant Circulate Travel the irrevocable, perpetual, worldwide, royalty-free right to:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Photograph, film, and record me during any tour or activity</li>
                  <li>Use such media for marketing, promotional, and commercial purposes</li>
                  <li>Share content in real-time on social media and digital platforms</li>
                  <li>Retain and use captured media indefinitely for future marketing</li>
                </ul>
                <p className="mt-2">
                  This includes use of my name, likeness, image, and voice. I waive any right to inspect or approve 
                  finished materials. If I wish to opt out, I will notify my tour guide at the start of the trip.
                </p>
              </section>

              {/* Section 4: Terms & Conditions */}
              <section>
                <h3 className="font-semibold text-primary mb-2">4. Terms & Conditions</h3>
                <p>
                  I have read and agree to Circulate Travel's Terms of Service, including the cancellation policy, 
                  payment terms, code of conduct, and all other provisions. I understand that violation of the code 
                  of conduct may result in removal from the trip without refund.
                </p>
              </section>

              {/* Section 5: Governing Law */}
              <section>
                <h3 className="font-semibold text-primary mb-2">5. Governing Law</h3>
                <p>
                  This agreement shall be governed by and construed in accordance with the laws of the United States. 
                  Any disputes arising from this agreement shall be resolved through binding arbitration.
                </p>
              </section>
            </div>

            {/* Agreement Checkboxes */}
            <div className="mt-8 space-y-4 border-t pt-6">
              <h3 className="font-semibold text-primary">Please confirm the following:</h3>
              
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="liability" 
                  checked={agreedToLiability}
                  onCheckedChange={setAgreedToLiability}
                />
                <Label htmlFor="liability" className="text-sm leading-relaxed cursor-pointer">
                  I have read and understand the <strong>Assumption of Risk & Liability Waiver</strong> (Section 1) 
                  and voluntarily agree to its terms. I understand that I am waiving certain legal rights.
                </Label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox 
                  id="photo" 
                  checked={agreedToPhoto}
                  onCheckedChange={setAgreedToPhoto}
                />
                <Label htmlFor="photo" className="text-sm leading-relaxed cursor-pointer">
                  I consent to the <strong>Photo & Video Release</strong> (Section 3) and grant Circulate Travel 
                  permission to use my likeness for marketing purposes.
                </Label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox 
                  id="terms" 
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                />
                <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                  I have read and agree to all <strong>Terms & Conditions</strong> and acknowledge the 
                  <strong> strong recommendation to purchase travel insurance with medical coverage</strong>.
                </Label>
              </div>
            </div>

            {/* Signature Section */}
            <div className="mt-8 border-t pt-6">
              <h3 className="font-semibold text-primary mb-4">Electronic Signature</h3>
              
              {/* Typed Name */}
              <div className="mb-4">
                <Label className="text-sm font-medium mb-2 block">Type Your Full Legal Name *</Label>
                <Input
                  placeholder="Enter your full legal name"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  className="max-w-md"
                />
              </div>

              {/* Signature Pad */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    Draw Your Signature *
                    {hasCompletedSignature && (
                      <span className="text-green-600 text-xs font-normal">(Captured ✓)</span>
                    )}
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="text-muted-foreground"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                </div>
                <div className={`border-2 border-dashed rounded-lg bg-white ${hasCompletedSignature ? 'border-green-500' : 'border-muted-foreground/30'}`}>
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      className: 'w-full h-32 cursor-crosshair',
                      style: { width: '100%', height: '128px' },
                      onMouseUp: handleCanvasMouseUp,
                      onTouchEnd: handleCanvasMouseUp
                    }}
                    penColor="black"
                    minWidth={1}
                    maxWidth={3}
                    onEnd={handleSignatureEnd}
                    onBegin={handleSignatureBegin}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Use your mouse or finger to sign above (required)
                </p>
              </div>

              <p className="text-xs text-muted-foreground">
                By signing above, I certify that I am the person named, I am at least 18 years of age (or have 
                parental/guardian consent), and I agree to be legally bound by this waiver and release agreement.
              </p>
            </div>
          </ScrollArea>
        </CardContent>

        {/* Footer Actions */}
        <div className="border-t p-4 flex gap-3 justify-end flex-shrink-0">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleAccept}
            disabled={!isComplete || loading}
            className="bg-secondary hover:bg-secondary/90"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Processing...
              </>
            ) : (
              'I Agree & Continue to Payment'
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};
